import { useEffect, useRef, useState } from 'react';

// Styles
import { ScheduledReportGridWrapper, ScheduledReportWrapper } from './ScheduledReport.styles';

// Components
import { ButtonPrimary, Loading, NoResults, StyledTitle, Table } from '../../common/components';
import InfiniteScroll from 'react-infinite-scroll-component';
import { EssAccountsTypes } from '../../types/account';
import { globalApiParams } from '../../common/api/globals';
import { ScheduledReportModal } from './ScheduledReportModal';
import { StatusBadge } from '../../common/components/StatusBadge/StatusBadge';
import { DeleteScheduledReportModal } from './DeleteScheduledReportModal';

import moment from 'moment';
import { REVERSED_DATE_FORMAT_HYPHEN } from '../../common/constants';
import {
  createScheduledReportRequests,
  updateScheduledReportRequests,
  deleteScheduledReportRequest,
} from '../../common/api/scheduledReportsApi';
import { useAuthState } from '../../providers/authProvider';

type ScheduledReportPageProps = {
  tpiUser: boolean;
  currentUserEmail: string;
  initialType: string;
  gridData: Array<Api.IScheduledReportRequest>;
  handlePagingChange: any;
  handleCustomerFilterChange: Function;
  handleLoadData: Function;
  customerData: Array<Common.IOption>;
  accountData: Array<Common.IOption>;
  siteData: Array<Common.IOption>;
  mpanMprnData: Array<Common.IOption>;
  isLoading?: boolean;
  hasMoreData: boolean;
  currentSortField: string;
  currentSortOrder: 'ASC' | 'DESC' | '';
  accountReferences: Array<any>;
  emails: Array<Api.IEmailRecipientScheduledReportRequest>;
};

const defaultProps = {
  tpiUser: false,
  gridData: [],
  customerData: [],
  accountData: [],
  siteData: [],
  mpanMprnData: [],
  initialType: EssAccountsTypes.Electricity,
  isLoading: false,
  hasMoreData: true,
  currentSortField: '',
  currentSortOrder: 'ASC',
};

const ScheduledReportPage = (props: ScheduledReportPageProps) => {
  const authContext = useAuthState();
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const [isLoadingEx, setIsLoadingEx] = useState(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const scrollableTable = useRef<any>();
  const sortDetails: Common.ISort = { field: props.currentSortField, order: props.currentSortOrder };

  const scrollToTop = () => {
    if (scrollableTable.current) {
      scrollableTable.current.scrollTo(0, 0);
    }
  };

  const staticTextTop =
    "Save time by scheduling automatic monthly HH data and invoice reports. Choose the reports you want and the dates you want them using the dropdown menus below. And then we'll send you an email each month directing you to your ‘Messages’, where your reports will be ready for downloading.";
  const staticTextBottom =
    "Once you've made your choices, your scheduled report selections will appear below. Schedule additional reports - or edit or delete existing scheduled reports - using the buttons below.";

  useEffect(() => {
    if (props.gridData.length <= globalApiParams.pageSize) {
      scrollToTop();
    }
  }, [props.gridData]);

  const handlePagingChange = () => {
    props.handlePagingChange();
  };

  let headers: Array<Common.HeaderItem> = [
    {
      title: 'Status',
      dataValue: 'taskStatus',
      width: 50,
      enableSort: false,
    },
    {
      title: 'Account',
      dataValue: 'customerAccountReference',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Download Report',
      dataValue: 'reportName',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Format',
      dataValue: 'fileType',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Frequency',
      dataValue: 'frequency',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Start Month',
      dataValue: 'startDate',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Delivery Day',
      dataValue: 'scheduledDayOfMonth',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Duration',
      dataValue: 'duration',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'End Month',
      dataValue: 'endMonth',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Requester Email Address',
      dataValue: 'requestedBy',
      minWidth: 50,
      enableSort: false,
    },
    {
      title: 'Additional Email Addresses',
      dataValue: 'emails',
      minWidth: 50,
      enableSort: false,
    },
  ];

  // Remove additionalEmailAddresses from headers for non-tpiUser
  if (!props.tpiUser) {
    const index = headers.findIndex((header: any) => header.dataValue === 'additionalEmailAddresses');
    headers.splice(index, 1);
  }

  const onClickSort = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
  };

  const handleSelectRow = (event: any, row: number) => {
    setSelectedRow(row);
  };

  const clearSelection = () => {
    setSelectedRow(-1);
  };

  const actionStyles = {
    width: '150px',
    opacity: '1',
  };

  const getSuffix = (day: number) => {
    let suffix = 'th';

    if (day === 1 || day === 21) suffix = 'st';
    else if (day === 2 || day === 22) suffix = 'nd';
    else if (day === 3 || day === 23) suffix = 'rd';

    return suffix;
  };

  const renderStatus = (value: string, row: Api.IScheduledReportRequest) => {
    const isLoaExpired = row.loaExpired;
    const displayValue = isLoaExpired ? 'LoA expired' : value;

    return (
      <td className='d-md-table-cell pb-0'>
        <StatusBadge
          className={''}
          title={displayValue}
          borderRadiusType={'squared'}
          enabledStatus={displayValue === 'Active'}
          disabledStatus={isLoaExpired || displayValue === 'Expired'}
        />
      </td>
    );
  };

  const renderReportName = (reportName: any) => {
    switch (reportName) {
      case 'HHData':
        return <td>{'HH data'}</td>;
      case 'InvoicePdfs':
        return <td>{"Invoice pdf's"}</td>;
      case 'InvoiceBackingData':
        return <td>{'Invoice backing data'}</td>;
      case 'SmartMeterData':
        return <td>{'Smart meter data'}</td>;
      case 'AMRData':
        return <td>{'AMR data'}</td>;
      default:
        return <td></td>;
    }
  };

  const renderFileType = (fileType: any) => {
    if (fileType === 'xlsx') {
      return <td>{'Excel'}</td>;
    } else {
      return <td>{fileType}</td>;
    }
  };

  const renderDay = (day: string) => {
    if (day) {
      const dayInt = parseInt(day);
      const suffix = getSuffix(dayInt);
      return (
        <td>
          {day}
          {suffix} of Month
        </td>
      );
    }
    return <td></td>;
  };

  const renderStartMonth = (month: string) => {
    const startDate = moment(month, REVERSED_DATE_FORMAT_HYPHEN).format('MMM YY');
    return <td>{startDate}</td>;
  };

  const renderEndMonth = (month: string, row: Api.IScheduledReportRequest) => {
    if (row.startDate && row.duration) {
      const startDate = moment(row.startDate, REVERSED_DATE_FORMAT_HYPHEN);
      const additionalMonths = row.duration - 1;
      const endDate = startDate.add(additionalMonths, 'month');
      return <td>{endDate.format('MMM YY')}</td>;
    }
    return <td></td>;
  };

  const renderAdditionalEmailAddresses = (emails: Array<any>) => {
    if (emails) {
      var formatedEmails = emails.join(', ');
      return <td>{formatedEmails}</td>;
    }
  };

  const handleCreateClick = async (reportData: any) => {
    await createScheduledReportRequests(process.env.REACT_APP_API_URL, authContext, reportData);
    clearSelection();
    props.handleLoadData();
  };

  const handleUpdateClick = async (reportData: any) => {
    await updateScheduledReportRequests(process.env.REACT_APP_API_URL, authContext, reportData);
    clearSelection();
    props.handleLoadData();
  };

  const handleDeleteClick = async () => {
    var id = props.gridData[selectedRow].id;
    await deleteScheduledReportRequest(process.env.REACT_APP_API_URL, authContext, id);

    clearSelection();
    handleModalClose();
    props.handleLoadData();
  };

  const handleModalClose = () => {
    setShowAddNewModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
  };

  const selectedItem: Api.IScheduledReportRequest = {
    createdDateTime: selectedRow >= 0 ? props.gridData[selectedRow].createdDateTime : null,
    customerAccountReference: selectedRow >= 0 ? props.gridData[selectedRow].customerAccountReference : '',
    duration: selectedRow >= 0 ? props.gridData[selectedRow].duration : 0,
    emailRecipientScheduledReportRequests:
      selectedRow >= 0 ? props.gridData[selectedRow].emailRecipientScheduledReportRequests : [],
    endMonthDate: selectedRow >= 0 ? props.gridData[selectedRow].endMonthDate : '',
    fileType: selectedRow >= 0 ? props.gridData[selectedRow].fileType : '',
    frequency: selectedRow >= 0 ? props.gridData[selectedRow].frequency : '',
    id: selectedRow >= 0 ? props.gridData[selectedRow].id : '',
    jobId: selectedRow >= 0 ? props.gridData[selectedRow].jobId : 0,
    loaExpired: selectedRow >= 0 ? props.gridData[selectedRow].loaExpired : null,
    reportName: selectedRow >= 0 ? props.gridData[selectedRow].reportName : '',
    requestedBy: selectedRow >= 0 ? props.gridData[selectedRow].requestedBy : '',
    requestedByUserId: selectedRow >= 0 ? props.gridData[selectedRow].requestedByUserId : '',
    scheduledDayOfMonth: selectedRow >= 0 ? props.gridData[selectedRow].scheduledDayOfMonth : 0,
    startDate: selectedRow >= 0 ? props.gridData[selectedRow].startDate : '',
    taskStatus: selectedRow >= 0 ? props.gridData[selectedRow].taskStatus : '',
    tpiReference: selectedRow >= 0 ? props.gridData[selectedRow].tpiReference : '',
    updatedDateTime: selectedRow >= 0 ? props.gridData[selectedRow].updatedDateTime : '',
    emails: selectedRow >= 0 ? props.gridData[selectedRow].emails : [],
  };

  const updatedGridData = props.gridData.map(item => ({
    ...item,
    disabled: item['loaExpired'],
  }));

  return (
    <ScheduledReportWrapper>
      <StyledTitle>Scheduled Reporting</StyledTitle>
      <div className='mt-3 mb-4 static-text'>
        <p>{staticTextTop}</p>
        <p>{staticTextBottom}</p>
      </div>

      <div className='justify-content-center align-items-center'>
        <div className='pt-4 pb-4 d-flex'>
          <div className='ml-auto'>
            <ButtonPrimary
              route='#'
              className=''
              title={'Add'}
              onClick={() => {
                setShowAddNewModal(true);
              }}
              disabled={false}
            />

            <ButtonPrimary
              route='#'
              className='ml-2'
              title={'Edit'}
              onClick={() => {
                setShowEditModal(true);
              }}
              disabled={selectedRow === null || selectedRow < 0}
            />

            <ButtonPrimary
              route='#'
              className='ml-2'
              title={'Delete'}
              onClick={() => {
                setShowDeleteModal(true);
              }}
              disabled={selectedRow === null || selectedRow < 0}
            />
          </div>
        </div>
      </div>

      {!props.isLoading && props.gridData.length === 0 && <NoResults />}
      <ScheduledReportGridWrapper>
        {props.gridData.length > 0 && (
          <>
            <InfiniteScroll
              dataLength={props.gridData.length}
              next={handlePagingChange}
              hasMore={props.hasMoreData}
              loader={''}
              scrollableTarget='scrollableDiv'
              scrollThreshold={0.9}
            >
              <div
                id='scrollableDiv'
                ref={scrollableTable}
                className='table-responsive table-fixed fixed-column columns-1'
              >
                <Table
                  isSelectable={false}
                  headers={headers}
                  rows={updatedGridData}
                  sortDetails={sortDetails}
                  onClickSort={onClickSort}
                  actionStyles={actionStyles}
                  selectedRowIndex={selectedRow}
                  handleSelectRow={handleSelectRow}
                  className='list table plain text-left'
                  rendertaskStatus={renderStatus}
                  renderscheduledDayOfMonth={renderDay}
                  renderstartDate={renderStartMonth}
                  renderendMonth={renderEndMonth}
                  renderemails={renderAdditionalEmailAddresses}
                  renderreportName={renderReportName}
                  renderfileType={renderFileType}
                />
              </div>
            </InfiniteScroll>
          </>
        )}

        {(props.isLoading || isLoadingEx) && <Loading overlay nowait />}
      </ScheduledReportGridWrapper>

      <ScheduledReportModal
        show={showAddNewModal}
        edit={false}
        tpiUser={props.tpiUser}
        accountReferences={props.accountReferences}
        emails={props.emails}
        requesterEmailAddress={props.currentUserEmail}
        selectedEmails={[]}
        onSubmit={(isAccepted: boolean, item: Api.IScheduledReportRequest) => {
          setShowAddNewModal(false);
          if (isAccepted) {
            var newItem: Api.IScheduledReportRequest = {
              reportName: item.reportName,
              customerAccountReference: item.customerAccountReference,
              scheduledDayOfMonth: item.scheduledDayOfMonth,
              fileType: item.fileType,
              startDate: item.startDate,
              duration: item.duration,
              frequency: item.frequency,
              emailRecipientScheduledReportRequests: item.emailRecipientScheduledReportRequests,
            };

            setIsLoadingEx(true);
            handleCreateClick(newItem);
            setIsLoadingEx(false);
          }
        }}
        onClose={handleModalClose}
      ></ScheduledReportModal>

      <ScheduledReportModal
        show={showEditModal}
        edit={true}
        tpiUser={props.tpiUser}
        emails={props.emails}
        accountReferences={props.accountReferences}
        requesterEmailAddress={selectedRow >= 0 ? props.gridData[selectedRow].requestedBy : ''}
        item={selectedItem}
        selectedEmails={selectedRow >= 0 ? props.gridData[selectedRow].emailRecipientScheduledReportRequests : []}
        onSubmit={(isAccepted: boolean, item: Api.IScheduledReportRequest) => {
          setShowEditModal(false);

          var editedItem: Api.IScheduledReportRequest = {
            id: item.id,
            reportName: item.reportName,
            customerAccountReference: item.customerAccountReference,
            scheduledDayOfMonth: item.scheduledDayOfMonth,
            fileType: item.fileType,
            startDate: item.startDate,
            duration: item.duration,
            frequency: item.frequency,
            emailRecipientScheduledReportRequests: item.emailRecipientScheduledReportRequests,
          };

          setIsLoadingEx(true);
          handleUpdateClick(editedItem);
          setIsLoadingEx(false);
        }}
        onClose={handleModalClose}
      ></ScheduledReportModal>

      <DeleteScheduledReportModal
        show={showDeleteModal}
        onYes={() => {
          handleDeleteClick();
        }}
        onNo={() => {
          setShowDeleteModal(false);
        }}
        bodyText={'Are you sure you want to delete the scheduled report, this action can not be undone?'}
      ></DeleteScheduledReportModal>
    </ScheduledReportWrapper>
  );
};

ScheduledReportPage.defaultProps = defaultProps;

export default ScheduledReportPage;
